<template>
	<div class="date">
		<VueDatePicker v-model="selectedDate" :format="format" :enable-time-picker="false" month-name-format="long"
			:clearable="false" position="right" range multi-calendars :preset-ranges="presetRanges" close-on-scroll
			:min-date="minDate" :max-date="maxDate" :disabled-dates="disabledDates" prevent-min-max-navigation
			@update:model-value="handleDateUpdate">
			<template #yearly="{ label, range, presetDateRange }" class="calendar-sidebar">
				<span @click="presetDateRange(range)">{{ label }}</span>
			</template>
			<template #action-preview="{ value }"> </template>
		</VueDatePicker>
	</div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useDashboardGeoStore } from '@/store/dashboard/dashboardGeo.js';
import { useMarketStore } from '@/store/global/market';
import { storeToRefs } from 'pinia';

const dashboardGeoStore = useDashboardGeoStore();
const { selectedDate, dates } = storeToRefs(dashboardGeoStore);

const marketStore = useMarketStore();
const { selectedMarket } = storeToRefs(marketStore);

// Convert available dates from timestamps to Date objects
const availableDates = computed(() => {
	if (!dates.value || !Array.isArray(dates.value)) {
		return [];
	}

	const result = dates.value
		.map(dateObj => {
			// Handle different possible formats
			let timestamp;
			if (typeof dateObj === 'number') {
				// Direct timestamp
				timestamp = dateObj;
			} else if (dateObj && typeof dateObj === 'object') {
				// Object with creation_date property
				timestamp = dateObj;
			} else {
				return null;
			}

			// Convert timestamp to milliseconds if it's in seconds
			const timestampMs = timestamp < 10000000000 ? timestamp * 1000 : timestamp;

			return new Date(timestampMs);
		})
		.filter(date => date && !isNaN(date.getTime())); // Filter out invalid dates

	// Add the specific date: Tue Sep 16 2025 18:00:00 GMT+0200 (Central European Summer Time)
	// const extraDate = new Date('2025-09-16T16:00:00.000Z'); // UTC time for 18:00:00 GMT+0200
	// result.push(extraDate);

	return result.sort((a, b) => a - b); // Sort dates in ascending order
});

// Get min and max dates from available dates
const minDate = computed(() => {
	if (availableDates.value.length === 0) return null;
	return availableDates.value[0];
});

const maxDate = computed(() => {
	if (availableDates.value.length === 0) return null;
	return availableDates.value[availableDates.value.length - 1];
});

// Create disabled dates function
const disabledDates = date => {
	// Check if the date is NOT in our available dates array
	return !availableDates.value.some(availableDate => {
		return date.toDateString() === availableDate.toDateString();
	});
};

// Create preset ranges based on available dates
const presetRanges = computed(() => {
	if (availableDates.value.length === 0) return [];

	const ranges = [];
	const sortedDates = [...availableDates.value];

	// First available date to last available date
	if (sortedDates.length >= 2) {
		ranges.push({
			label: 'Toutes les dates',
			range: [sortedDates[0], sortedDates[sortedDates.length - 1]],
		});
	}

	// Last 2 dates if available
	if (sortedDates.length >= 2) {
		ranges.push({
			label: '2 dernières dates',
			range: [sortedDates[sortedDates.length - 2], sortedDates[sortedDates.length - 1]],
		});
	}

	// Last 3 dates if available
	if (sortedDates.length >= 3) {
		ranges.push({
			label: '3 dernières dates',
			range: [sortedDates[sortedDates.length - 3], sortedDates[sortedDates.length - 1]],
		});
	}

	// Last 4 dates if available
	if (sortedDates.length >= 4) {
		ranges.push({
			label: '4 dernières dates',
			range: [sortedDates[sortedDates.length - 4], sortedDates[sortedDates.length - 1]],
		});
	}

	// Last 5 dates if available
	if (sortedDates.length >= 5) {
		ranges.push({
			label: '5 dernières dates',
			range: [sortedDates[sortedDates.length - 5], sortedDates[sortedDates.length - 1]],
		});
	}

	// ranges.push({
	// 	label: 'pour kevin',
	// 	range: [
	// 		new Date('2025-09-16T16:00:00.000Z'),
	// 		new Date('2025-09-16T16:00:00.000Z')
	// 	],
	// });

	return ranges;
});

watch(
	selectedDate,
	newDates => {
		dashboardGeoStore.fetchAllGeoData?.(selectedMarket.value, false);
	},
	{ immediate: true },
);

// Format function for display
const format = date => {
	if (!date || !Array.isArray(date) || date.length !== 2) return '';

	const formatSingleDate = d => {
		return d.toLocaleDateString('fr-FR', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
		});
	};

	return `${formatSingleDate(date[0])} > ${formatSingleDate(date[1])}`;
};

// Handle date updates
const handleDateUpdate = modelData => {
	if (!modelData || !Array.isArray(modelData) || modelData.length !== 2) {
		return;
	}

	// Ensure dates are Date objects
	let fromDate = new Date(modelData[0]);
	let toDate = new Date(modelData[1]);

	// Swap dates if 'from' is later than 'to'
	if (fromDate > toDate) {
		[fromDate, toDate] = [toDate, fromDate];
	}

	// Validate that both dates are in our available dates
	const isFromDateValid = availableDates.value.some(d => d.toDateString() === fromDate.toDateString());
	const isToDateValid = availableDates.value.some(d => d.toDateString() === toDate.toDateString());

	if (isFromDateValid && isToDateValid) {
		// Update the store with the valid range
		selectedDate.value = [fromDate, toDate];
	} else {
		console.warn('Selected dates are not in the available dates list');
		// Optionally reset to default selection instead of null
		if (availableDates.value.length >= 2) {
			const sortedDates = [...availableDates.value];
			selectedDate.value = [sortedDates[sortedDates.length - 2], sortedDates[sortedDates.length - 1]];
		}
	}
};
</script>

<style lang="scss" scoped>
.date {
	z-index: 1;
}
</style>